import React from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ProjectTodo, setTodoStatusAction } from '../ducks';
import { RowState } from '../selectors';
import { DragPreview } from './Outline';
import { ProjectRowContent } from './ProjectRowContent';
import { ProjectStatusCell } from './ProjectStatusCell';
import { OwnerAvatar } from '@/features/calendar/components/OwnerAvatar';
import { User } from '@/infra/types/domain';

interface TodoRowProps {
  todo: ProjectTodo;
  depth: number;
  connectors: boolean[];
  hasChildren: boolean;
  collapsed: boolean;
  onToggleCollapsed: (id: string) => void;
  onAddChild: (parentId: string) => void;
  onDelete: (todoId: string) => void;
  projectId: string;
  // Household members; the owner avatar renders only when there's >1.
  users: User[];
  // Classification (ready/blocked/done) is computed once per project in
  // Outline and passed down — avoids each row re-classifying the whole tree.
  rowState: RowState;
  // Scheduled marker — same read-only base as the inbox view.
  planned: boolean;
  dragPreview: DragPreview | null;
}

// Edit-mode adapter for the project detail Outline. Owns the @dnd-kit
// `useSortable` reorder/reparent binding and the editable affordances (status
// toggle, drag grip, add-subtask, delete), then renders the shared
// ProjectRowContent. The read-only Inbox uses the same ProjectRowContent via
// InboxProjectRow with a different (schedule-drag) binding.
export function TodoRow({
  todo,
  depth,
  connectors,
  hasChildren,
  collapsed,
  onToggleCollapsed,
  onAddChild,
  onDelete,
  users,
  rowState,
  planned,
  dragPreview,
}: TodoRowProps) {
  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: todo.id });

  const isBlocked = rowState === 'blocked';
  const isDone = todo.status === 'done';

  const onToggleStatus = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    if (isBlocked && !isDone) return;
    const prev = todo.status;
    const next = prev === 'done' ? 'pending' : 'done';
    dispatch(setTodoStatusAction({ todoId: todo.id, status: next, prevStatus: prev }));
  };

  const openDetail = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('todo', todo.id);
      return next;
    });
  };

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  let previewColumn: number | null = null;
  let previewIllegal = false;
  if (dragPreview && dragPreview.overId === todo.id && dragPreview.activeId !== todo.id) {
    const offset =
      dragPreview.indent === 'nest' ? 1 : dragPreview.indent === 'unnest' ? -1 : 0;
    previewColumn = Math.max(0, depth + offset);
    previewIllegal = dragPreview.illegal;
  }

  const rowClassName = `todo-row--${rowState}${previewColumn != null ? ' todo-row--drop-target' : ''}${previewIllegal ? ' todo-row--drop-illegal' : ''}`;

  return (
    <ProjectRowContent
      ref={setNodeRef}
      rowClassName={rowClassName}
      rowProps={{
        style,
        role: 'button',
        tabIndex: 0,
        onClick: openDetail,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openDetail();
          }
        },
        'data-todo-id': todo.id,
        'data-parent-id': todo.parent_todo_id ?? '__root__',
        'data-row-state': rowState,
        title: previewIllegal ? 'Cannot nest a parent inside its own descendant' : undefined,
      }}
      connectors={connectors}
      hasChildren={hasChildren}
      collapsed={collapsed}
      todoId={todo.id}
      onToggleCollapsed={onToggleCollapsed}
      grip={
        <span
          className="todo-row__grip"
          aria-label="Drag to reorder or indent"
          onClick={(e) => e.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          ⋮⋮
        </span>
      }
      statusCell={
        <ProjectStatusCell
          todo={todo}
          rowState={rowState}
          hasChildren={hasChildren}
          editable
          onToggleStatus={onToggleStatus}
        />
      }
      title={todo.title}
      planned={planned}
      afterTitle={
        <>
          {users.length > 1 && (
            <OwnerAvatar ownerId={todo.owner_id} users={users} size="sm" />
          )}
          <button
            type="button"
            className="todo-row__add-child"
            aria-label="Add subtask"
            title="Add subtask"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onAddChild(todo.id);
            }}
          >
            +
          </button>
          <button
            type="button"
            className="todo-row__delete"
            aria-label="Delete task"
            title="Delete task"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(todo.id);
            }}
          >
            ✕
          </button>
        </>
      }
      dropCaret={
        previewColumn != null ? (
          <span
            className={`todo-row__drop-caret${previewIllegal ? ' todo-row__drop-caret--illegal' : ''}`}
            aria-hidden="true"
            style={{ left: `${previewColumn * 24}px` }}
          />
        ) : null
      }
    />
  );
}
